import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { ShareSummaryItem } from '@elara/client-core/lib/share-links';
import type { TripModel } from '@elara/client-core/models/TripModel';

import React from 'react';

import {
  buildSharedItineraryUrl,
  SHARE_ITINERARY_DESCRIPTION,
  SHARED_ITINERARY_EXCLUDES,
  SHARED_ITINERARY_INCLUDES,
} from '@elara/client-core/lib/share-links';

import { useToastState, useTripsState } from '#states/hooks.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  IconSizes,
  MaterialIcon,
} from '#ui/index.js';
import { Switch } from '#ui/switch.js';

/** One column of the shared / never-shared summary; the column tint carries which side it is. */
function ShareList({
  title,
  iconClassName,
  items,
}: {
  title: string;
  iconClassName: string;
  items: readonly ShareSummaryItem[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">{title}</div>
      <ul className="flex flex-col gap-3 text-sm">
        {items.map((item) => (
          <li key={item.label} className="flex items-start gap-2.5">
            {/* A box one text line tall (text-sm = 20px) keeps the icon centred on the label line. */}
            <span className="flex h-5 flex-shrink-0 items-center">
              <MaterialIcon icon={item.icon} size={IconSizes.xs} className={iconClassName} />
            </span>
            <div className="flex flex-col">
              <span>{item.label}</span>
              {item.detail && <span className="text-xs text-muted-foreground text-pretty">{item.detail}</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Lets the trip owner turn a public read-only link to their itinerary on and off — e.g. to text a
 * family member so they know where the traveler is — and copy it. States plainly what the
 * recipient sees and what never leaves the account. Turning the link off kills it; turning it back on
 * mints a new one.
 */
export function ShareItineraryDialog({ trip }: { trip: ClientInstanceOf<TripModel> }): React.ReactNode {
  const tripsState = useTripsState();
  const toastState = useToastState();
  const token = tripsState.shareToken;
  const isEnabled = Boolean(token);
  // The state the owner just switched to. The flow resolves before the trip listener delivers the new
  // token, so the switch holds this position (spinning) until the store catches up.
  const [pendingEnabled, setPendingEnabled] = React.useState<boolean>();

  React.useEffect(() => {
    if (pendingEnabled === isEnabled) setPendingEnabled(undefined);
  }, [pendingEnabled, isEnabled]);

  if (!tripsState.isShareDialogOpen) return undefined;

  // Web links use this page's origin so preview channels and local dev produce working URLs.
  const shareUrl = token ? buildSharedItineraryUrl(token, window.location.origin) : undefined;
  const isSettling = pendingEnabled !== undefined && pendingEnabled !== isEnabled;
  const isBusy = tripsState.isUpdatingShareLink || isSettling;
  const switchChecked = isSettling ? pendingEnabled : isEnabled;

  async function setEnabled(enabled: boolean) {
    setPendingEnabled(enabled);
    const succeeded = enabled
      ? (await tripsState.enableShareLink(trip.id)) !== undefined
      : await tripsState.disableShareLink(trip.id);
    // On failure the store shows an error toast; snap the switch back to the real state.
    if (!succeeded) {
      setPendingEnabled(undefined);
      return;
    }
    if (!enabled) toastState.addToast({ title: 'Link turned off', description: 'The old link no longer works.' });
  }

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      tripsState.recordItineraryShared(trip.id, 'copy_link');
      toastState.addToast({ title: 'Link copied', description: 'Paste it into a message or email.' });
    } catch {
      toastState.addToast({ title: 'Copy failed', description: 'Please try again.', variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && tripsState.closeShareDialog()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Share your itinerary</DialogTitle>
          <DialogDescription className="text-pretty">{SHARE_ITINERARY_DESCRIPTION}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ShareList title="What’s shared" iconClassName="text-success" items={SHARED_ITINERARY_INCLUDES} />
          <ShareList title="Never shared" iconClassName="text-destructive" items={SHARED_ITINERARY_EXCLUDES} />
        </div>

        <div className="flex flex-col gap-4 border-t pt-4">
          <div className="flex items-center gap-3">
            <Switch
              id="share-link-toggle"
              checked={switchChecked}
              isBusy={isBusy}
              onCheckedChange={(checked) => void setEnabled(checked)}
            />
            <label htmlFor="share-link-toggle" className="text-sm font-medium cursor-pointer">
              Public link{' '}
              <span className={isEnabled ? 'text-success' : 'text-destructive'}>
                {isEnabled ? 'enabled' : 'disabled'}
              </span>
            </label>
          </div>

          {shareUrl && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => void copyLink()}
                aria-label="Copy share link"
                data-testid="share-itinerary-url"
                className="w-full flex items-center gap-3 rounded-md border bg-background-secondary px-3 py-2.5 text-left text-sm hover:border-accent"
              >
                {/* `sentry-mask`: session replays of the trip page must not record the bearer link. */}
                <span className="sentry-mask flex-1 min-w-0 break-all">{shareUrl}</span>
                <MaterialIcon icon="content_copy" className="flex-shrink-0 text-muted-foreground" />
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
